/**
 * **C09 · The 1099 letter and the W-9 chase letter** (Stream C, Documents.dc.html): one half-letter
 * page per payee, laid out by the `payee_letters` engine from Money's 1099 watch, its vendors, Bills
 * to pay and the W-9 worklist, signed by the finance director named in Roles. Scored against the
 * drawing by walk 99g.
 */
import type { ReactNode } from 'react';
import { FestivalClock, ten99Letters, w9ChaseLetters } from '@cider/domain/engines';
import { useLoad } from '../views/money/shared.js';
import { usePrinted } from './printed.js';
import { DrawnPages } from './drawn-doc.js';
import { holder, type RoleRow } from './aod-package.js';

interface Vendor {
  id: string;
  name: string;
  contactName: string | null;
}

/** `/print/doc/1099-letters:<fy>` — January's letters for the calendar year the festival was in. */
export function Ten99Letters({ fy }: { fy: number }): ReactNode {
  const year = fy - 1;
  const watch = useLoad<{ rows: { vendorId: string; paidCents: number; status: string }[] }>(
    `/money/vendors/1099/${String(year)}`,
  );
  const vendors = useLoad<{ vendors: Vendor[] }>('/money/vendors?all=true');
  const roles = useLoad<RoleRow[]>(`/admin/roles/year?year=${String(year)}`);
  const ready = [watch, vendors, roles].every((l) => l.data !== null || l.error !== null);
  usePrinted('doc-1099-letters', ready, { year });
  if (!ready) return <p>Reading the 1099 watch…</p>;
  const byId = new Map((vendors.data?.vendors ?? []).map((v) => [v.id, v]));
  const pages = ten99Letters({
    year,
    day: FestivalClock.calendarDay(new Date()),
    payees: watch.data
      ? watch.data.rows
          .filter((r) => r.status === 'form due')
          .map((r) => ({
            payee: byId.get(r.vendorId)?.name ?? 'a vendor',
            contact: byId.get(r.vendorId)?.contactName ?? null,
            paidCents: r.paidCents,
          }))
      : null,
    financeDirector: holder(roles.data, 'finance_director'),
  });
  return <DrawnPages pages={pages} size="half" />;
}

interface Pick {
  vendorId: string;
  payee: string;
  paper: string;
  what: string;
  cents: number;
}

/** `/print/doc/w9-chase:<fy>` — a letter to each 1099 vendor a ready check waits on for a W-9. */
export function W9Chase({ fy }: { fy: number }): ReactNode {
  const toPay = useLoad<{ due: Pick[]; held: Pick[] }>(`/money/bills/to-pay?fy=${String(fy)}`);
  const w9 = useLoad<{ rows: { vendorId: string; onFile: boolean }[] }>('/money/vendors/w9');
  const roles = useLoad<RoleRow[]>(`/admin/roles/year?year=${String(fy - 1)}`);
  const ready = [toPay, w9, roles].every((l) => l.data !== null || l.error !== null);
  usePrinted('doc-w9-chase', ready, { year: fy - 1 });
  if (!ready) return <p>Reading Bills to pay…</p>;
  const missing = new Set((w9.data?.rows ?? []).filter((r) => !r.onFile).map((r) => r.vendorId));
  const owed = new Map<string, Pick[]>();
  for (const b of toPay.data?.due ?? [])
    if (missing.has(b.vendorId)) owed.set(b.vendorId, [...(owed.get(b.vendorId) ?? []), b]);
  const words = (xs: string[]): string => [...new Set(xs)].join(' · ');
  const pages = w9ChaseLetters({
    day: FestivalClock.calendarDay(new Date()),
    owed:
      toPay.data && w9.data
        ? [...owed.values()].map((bs) => ({
            payee: bs[0]?.payee ?? 'a vendor',
            cents: bs.reduce((n, b) => n + b.cents, 0),
            what: words(bs.map((b) => b.what)),
            paper: words(bs.map((b) => b.paper.split(' · ')[0] ?? b.paper)),
          }))
        : null,
    financeDirector: holder(roles.data, 'finance_director'),
  });
  return <DrawnPages pages={pages} size="half" />;
}
